import { SpinningLoader } from "risa-oasis-ui_v2";
import type { SupportTicketListEntry } from "../supportTicketTypes";
import SupportTicketListItem from "./supportTicketListItem";

/**
 * Props for {@link SupportTicketList}.
 */
interface SupportTicketListProps {
  /** Ticket rows rendered in the sidebar. */
  tickets: SupportTicketListEntry[];
  /** Currently selected ticket id. */
  selectedId: string;
  /** Called when the user selects a ticket row. */
  onSelect: (ticketId: string) => void;
  /** Whether the ticket list or search request is in flight. */
  loading?: boolean;
  /** Error message from the ticket list request, if any. */
  error?: string | null;
  /** Optional class name for the outer list container. */
  className?: string;
}

/**
 * Bordered, vertically scrollable list of support tickets for the sidebar.
 *
 * @param props - Ticket rows, selection state, fetch status, and select handler.
 * @returns Scrollable support ticket sidebar list.
 */
const SupportTicketList = ({
  tickets,
  selectedId,
  onSelect,
  loading = false,
  error = null,
  className = "",
}: SupportTicketListProps) => {
  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex h-full min-h-[12rem] flex-col items-center justify-center gap-2">
          <SpinningLoader />
          <div className="text-sm text-primaryGray-7">Loading tickets...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="px-4 py-6 text-sm text-red-600">{error}</div>
      );
    }

    if (tickets.length === 0) {
      return (
        <div className="px-4 py-6 text-sm text-primaryGray-7">
          No support tickets found.
        </div>
      );
    }

    return tickets.map((ticket) => (
      <SupportTicketListItem
        key={ticket.id}
        ticket={ticket}
        isSelected={ticket.id === selectedId}
        onSelect={onSelect}
      />
    ));
  };

  return (
    <div
      className={`support-tickets-list flex h-full min-h-0 flex-col overflow-hidden rounded border border-primaryGray-14 bg-white ${className}`.trim()}
    >
      <div className="support-tickets-list__scroll min-h-0 flex-1 overflow-y-auto">
        {renderBody()}
      </div>
    </div>
  );
};

export default SupportTicketList;
